import React from 'react'
import PageGetTenant from '../../pages/PageGetTenant'
import EqxLoading from '../../shared/components/EqxLoading'
import { useTenantlessApiContext } from './AppTenantlessApi'
import { AppTenantlessDeployment, readStoredEnvironment, useTenantlessDeploymentContext } from './AppTenantlessDeploymentProvider'
import { clearPostAuthReturn, savePostAuthReturnFromWindow } from './authReturnPath'
import { getTenantPathPrefix, isOrgPickerPath, parseTenantPathSegment } from './pathTenant'
import { useWindowPathname } from './useWindowPathname'

const TENANT_ERROR_QUERY_PARAM = 'tenantError'

const RedirectToOrgPickerWithError = () => {
    React.useEffect(() => {
        const params = new URLSearchParams()
        params.set(TENANT_ERROR_QUERY_PARAM, '1')
        window.location.replace(`/switch?${params.toString()}`)
    }, [])

    return <EqxLoading />
}

/** Full deployment including tenant (resolved from `/_…` URL segment via lookup API). */
export interface AppDeployment extends AppTenantlessDeployment {
    tenantID: string
    /** URL prefix for this organisation, e.g. `/_MyOrg` (React Router basename). */
    organisationURLPrefix: string
    setTenantID: (tenantID: string) => void
    clearTenantID: () => void
    clear: () => void
}

interface TenantSession {
    tenantID: string
    organisationURLPrefix: string | null
    tenantLookupLoading: boolean
    tenantLookupError: string | null
    setTenantID: (tenantID: string) => void
    clearTenantID: () => void
    clear: () => void
}

const deploymentContext = React.createContext<AppDeployment | undefined>(undefined)

export const useDeploymentContext = () => {
    const contextInstance = React.useContext(deploymentContext)
    if (contextInstance === undefined) {
        throw new Error('useDeploymentContext must be used within a tenanted branch of AppDeploymentProvider')
    }
    return contextInstance
}

const tenantSessionContext = React.createContext<TenantSession | undefined>(undefined)

export const useTenantSessionContext = () => {
    const contextInstance = React.useContext(tenantSessionContext)
    if (contextInstance === undefined) {
        throw new Error('useTenantSessionContext must be used within AppDeploymentProvider')
    }
    return contextInstance
}

const useTenantID = () => {
    const deployment = useDeploymentContext()
    return deployment.tenantID
}

function shouldRedirectBarePathToPicker(pathname: string): boolean {
    if (isOrgPickerPath(pathname)) return false
    if (getTenantPathPrefix(pathname)) return false
    const first = pathname.split('/').filter(Boolean)[0]
    if (first && /\.[a-zA-Z0-9]+$/.test(first)) return false
    return true
}

const TenantSessionProvider = (props: { children: React.ReactNode }) => {
    const base = useTenantlessDeploymentContext()
    const api = useTenantlessApiContext()
    const pathname = useWindowPathname()

    const organisationURLPrefix = getTenantPathPrefix(pathname)
    const pathTenant = React.useMemo(() => parseTenantPathSegment(pathname), [pathname])
    const displayForLookup = pathTenant.kind === 'ok' ? pathTenant.displayId : null

    const [tenantID, setTenantIDState] = React.useState('')
    const [tenantLookupLoading, setTenantLookupLoading] = React.useState(false)
    const [tenantLookupError, setTenantLookupError] = React.useState<string | null>(null)

    React.useEffect(() => {
        if (!organisationURLPrefix || !displayForLookup) {
            setTenantIDState('')
            setTenantLookupLoading(false)
            setTenantLookupError(null)
            return
        }

        if (pathTenant.kind === 'error') {
            setTenantIDState('')
            setTenantLookupLoading(false)
            setTenantLookupError(null)
            return
        }

        if (pathTenant.kind !== 'ok') {
            return
        }

        let cancelled = false
        setTenantLookupLoading(true)
        setTenantLookupError(null)
        api.endpoints
            .tenantLookupByDisplayId()
            .execute(api, { tenantDisplayID: displayForLookup })
            .then((res) => {
                if (cancelled) return
                setTenantIDState(res.tenantID.trim().toUpperCase())
                setTenantLookupLoading(false)
            })
            .catch(() => {
                if (cancelled) return
                setTenantLookupError("Couldn't resolve that organisation from the URL.")
                setTenantIDState('')
                setTenantLookupLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [organisationURLPrefix, displayForLookup, pathTenant, pathname, api])

    const setTenantID = React.useCallback((id: string) => {
        setTenantIDState(id.trim().toUpperCase())
    }, [])

    const clearTenantID = React.useCallback(() => {
        setTenantIDState('')
    }, [])

    const clear = React.useCallback(() => {
        const env = readStoredEnvironment()
        if (env) base.setEnvironment(env)
        else base.clearEnvironment()
        base.setInstance('')
        setTenantIDState('')
        clearPostAuthReturn()
        window.location.assign('/switch')
    }, [base])

    const session: TenantSession = {
        tenantID,
        organisationURLPrefix,
        tenantLookupLoading,
        tenantLookupError,
        setTenantID,
        clearTenantID,
        clear,
    }

    return <tenantSessionContext.Provider value={session}>{props.children}</tenantSessionContext.Provider>
}

const BareLoginRedirect = () => {
    React.useEffect(() => {
        savePostAuthReturnFromWindow()
        window.location.replace('/switch')
    }, [])
    return <EqxLoading />
}

/** Org picker shown when tenancy could not be resolved from the URL alone; remembers return path once. */
const PageGetTenantWithReturnCapture = () => {
    const pathnameCaptured = useWindowPathname()
    React.useEffect(() => {
        if (!isOrgPickerPath(pathnameCaptured)) {
            savePostAuthReturnFromWindow()
        }
    }, [pathnameCaptured])
    return <PageGetTenant />
}

const AppDeploymentProviderContent = (props: { children: React.ReactNode }) => {
    const base = useTenantlessDeploymentContext()
    const session = useTenantSessionContext()
    const ContextProvider = deploymentContext.Provider
    const pathname = useWindowPathname()

    if (isOrgPickerPath(pathname)) {
        return <PageGetTenant />
    }

    if (shouldRedirectBarePathToPicker(pathname)) {
        return <BareLoginRedirect />
    }

    const prefix = getTenantPathPrefix(pathname)
    const pathTenant = parseTenantPathSegment(pathname)

    if (prefix && pathTenant.kind === 'error') {
        return <RedirectToOrgPickerWithError />
    }

    const displayId = pathTenant.kind === 'ok' ? pathTenant.displayId : null
    const needsLookup = !!(prefix && displayId)

    if (needsLookup && session.tenantLookupLoading) {
        return <EqxLoading />
    }

    if (needsLookup && session.tenantLookupError) {
        return <RedirectToOrgPickerWithError />
    }

    if (needsLookup && !session.tenantID) {
        return <EqxLoading />
    }

    if (!prefix || !session.tenantID) {
        return <PageGetTenantWithReturnCapture />
    }

    const instanceFromUrl = pathTenant.kind === 'ok' ? pathTenant.instance : ''

    const value: AppDeployment = {
        ...base,
        instance: instanceFromUrl,
        tenantID: session.tenantID,
        organisationURLPrefix: prefix,
        setTenantID: session.setTenantID,
        clearTenantID: session.clearTenantID,
        clear: session.clear,
    }

    return <ContextProvider value={value}>{props.children}</ContextProvider>
}

const AppDeploymentProvider = (props: { children: React.ReactNode }) => {
    return (
        <TenantSessionProvider>
            <AppDeploymentProviderContent>{props.children}</AppDeploymentProviderContent>
        </TenantSessionProvider>
    )
}

export { AppDeploymentProvider as default, useTenantID }

